import { useEffect, useRef, useState } from "react";

import "./Dashboard.css";
import "./Dashboard.mobile.css";


// ─── Composants ───────────────────────────────────────────────────────────────

import Profil from "./DashboardSections/Parcours/Profil.js";
import EducationManifesto from "./DashboardSections/Parcours/EducationManifesto.js";

import Expertise from "./DashboardSections/Competences/Outils.js";
import LanguesParlees from "./DashboardSections/Competences/LanguesParlees.js";

import Process from "./DashboardSections/Methodologie/Process.js";
import Architecture from "./DashboardSections/Methodologie/Architecture.js";
import Conseil from "./DashboardSections/Methodologie/Conseil.js";

import Relationnel from "./DashboardSections/Soft Skills/Relationnel.js";
import Organisation from "./DashboardSections/Soft Skills/Organisation.js";


// ─── Structure du site ────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: "parcours",
    label: "Parcours",
    number: "01",

    items: [
      {
        id: "profil",
        label: "Profil",
        desc: "Identité, positionnement, ancrage",
        component: <Profil />,
      },

      {
        id: "autoformation",
        label: "Formation",
        desc: "Manifeste éducatif & curiosités",
        component: <EducationManifesto />,
      },
    ],
  },

  {
    id: "competences",
    label: "Compétences",
    number: "02",

    items: [
      {
        id: "outils",
        label: "Outils",
        desc: "Stack technique & expertise",
        component: <Expertise />,
      },

      {
        id: "langues",
        label: "Langues parlées",
        desc: "Communication multilingue",
        component: <LanguesParlees />,
      },
    ],
  },

  {
    id: "methodologie",
    label: "Méthodologie",
    number: "03",

    items: [
      {
        id: "process",
        label: "Process",
        desc: "Flux de travail & rigueur",
        component: <Process />,
      },

      {
        id: "architecture",
        label: "Architecture",
        desc: "Structures & systèmes",
        component: <Architecture />,
      },

      {
        id: "conseil",
        label: "Conseil",
        desc: "Posture & recommandations",
        component: <Conseil />,
      },
    ],
  },

  {
    id: "softskills",
    label: "Soft Skills",
    number: "04",

    items: [
      {
        id: "relationnel",
        label: "Relationnel",
        desc: "Collaboration & communication",
        component: <Relationnel />,
      },

      {
        id: "organisation",
        label: "Organisation",
        desc: "Priorités & discipline",
        component: <Organisation />,
      },
    ],
  },
];


// ─── Nombre total d'items ─────────────────────────────────────────────────────

const TOTAL = SECTIONS.reduce(
  (acc, section) => acc + section.items.length,
  0
);


// ─── Sous-composants ──────────────────────────────────────────────────────────

const SectionHeader = ({ number, label }) => (
  <div className="di-section-header">
    <span className="di-section-num">
      {number}
    </span>

    <span className="di-section-label">
      {label}
    </span>
  </div>
);


// ─── Ligne cliquable ──────────────────────────────────────────────────────────

const ItemRow = ({
  idx,
  label,
  desc,
  isOpen,
  onToggle,
}) => (
  <div
    className={`di-item-row${isOpen ? " di-item-row--open" : ""}`}
    onClick={onToggle}
    role="button"
    tabIndex={0}
    aria-expanded={isOpen}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle();
      }
    }}
  >
    <span className="di-item-idx">
      {idx}
    </span>

    <div className="di-item-body">
      <span className="di-item-label">
        {label}
      </span>

      <span className="di-item-desc">
        {desc}
      </span>
    </div>

    <span
      className="di-item-arrow"
      aria-hidden="true"
    >
      →
    </span>
  </div>
);


// ─── Panneau ouvert ───────────────────────────────────────────────────────────

const AccordionPanel = ({
  sectionLabel,
  itemLabel,
  onClose,
  children,
}) => (
  <div className="di-panel">

    <div className="di-panel-bar">

      <span className="di-panel-crumb">
        <strong>
          {sectionLabel}
        </strong>

        &nbsp;/&nbsp;

        {itemLabel}
      </span>

      <button
        className="di-panel-close"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        ✕ fermer
      </button>

    </div>

    <div className="di-panel-content">
      {children}
    </div>

  </div>
);


// ─── Composant principal ──────────────────────────────────────────────────────

export default function DashboardIndex() {

  const [openId, setOpenId] = useState(null);

  /*
   * On conserve une référence DOM pour chaque item.
   *
   * Exemple :
   *
   * itemRefs.current.process
   * itemRefs.current.profil
   * itemRefs.current.langues
   */
  const itemRefs = useRef({});


  // ─────────────────────────────────────────────────────
  // OUVRIR / FERMER UN ITEM
  // ─────────────────────────────────────────────────────

  const toggle = (id) => {
    setOpenId((previousId) => {
      if (previousId === id) {
        return null;
      }

      return id;
    });
  };


  // ─────────────────────────────────────────────────────
  // SCROLL AUTOMATIQUE APRÈS OUVERTURE
  // ─────────────────────────────────────────────────────

  useEffect(() => {

    if (!openId) {
      return;
    }

    const element = itemRefs.current[openId];

    if (!element) {
      return;
    }

    /*
     * On attend deux frames.
     *
     * La première laisse React injecter le panneau.
     * La deuxième laisse le navigateur recalculer le layout.
     *
     * On récupère ensuite la vraie position de l'item.
     */
    const firstFrame = requestAnimationFrame(() => {

      const secondFrame = requestAnimationFrame(() => {

        const navbar =
          document.querySelector(".navbar-root");

        const navbarHeight =
          navbar?.getBoundingClientRect().height ?? 0;

        const elementTop =
          element.getBoundingClientRect().top +
          window.scrollY;

        /*
         * Petit espace visuel entre la navbar
         * et l'item ouvert.
         */
        const gap = 8;

        const targetPosition =
          Math.max(
            0,
            elementTop - navbarHeight - gap
          );

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });

      });

      /*
       * Cleanup de la deuxième frame
       * si le composant change rapidement.
       */
      return () => {
        cancelAnimationFrame(secondFrame);
      };

    });

    return () => {
      cancelAnimationFrame(firstFrame);
    };

  }, [openId]);


  const isSomethingOpen =
    openId !== null;


  let globalIdx = 0;


  return (
    <div
      className={`di-root${
        isSomethingOpen
          ? " di-root--has-open"
          : ""
      }`}
    >

      {/* ==================================================
          HEADER
      ================================================== */}

      <header className="di-header">

        <span className="di-site-title">
          INDEX
        </span>

        <span className="di-tag">
          v{new Date().getFullYear()}
        </span>

        <span className="di-counter">
          {TOTAL} sections
        </span>

      </header>


      {/* ==================================================
          SECTIONS
      ================================================== */}

      <div className="di-sections-wrapper">

        {SECTIONS.map((section) => {

          const hasOpenItem =
            section.items.some(
              (item) => item.id === openId
            );


          return (
            <div
              className={`di-section${
                hasOpenItem
                  ? " di-section--has-open"
                  : ""
              }`}
              key={section.id}
            >

              <SectionHeader
                number={section.number}
                label={section.label}
              />


              {section.items.map((item) => {

                globalIdx++;

                const idx =
                  String(globalIdx).padStart(
                    2,
                    "0"
                  );

                const isOpen =
                  openId === item.id;


                return (
                  <div
                    key={item.id}

                    ref={(element) => {
                      if (element) {
                        itemRefs.current[item.id] =
                          element;
                      } else {
                        delete itemRefs.current[item.id];
                      }
                    }}

                    className={`di-item-wrap${
                      isOpen
                        ? " di-item-wrap--open"
                        : ""
                    }`}
                  >

                    <ItemRow
                      idx={idx}
                      label={item.label}
                      desc={item.desc}
                      isOpen={isOpen}
                      onToggle={() => toggle(item.id)}
                    />


                    {isOpen && (
                      <AccordionPanel
                        sectionLabel={section.label}
                        itemLabel={item.label}
                        onClose={() => {
                          setOpenId(null);
                        }}
                      >
                        {item.component}
                      </AccordionPanel>
                    )}

                  </div>
                );
              })}

            </div>
          );
        })}

      </div>

    </div>
  );
}